import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Space, message, Image, Alert } from 'antd';
import { userApi } from '../api/user';
import { DoctorProfile, UpdateDoctorProfileRequest } from '../api/user';
import { useInterval } from '../util/hooks';
import { getPasswordHash } from '../util/crypto';

const Profile: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<DoctorProfile | null>(null);
  
  // 微信绑定相关状态
  const [isWechatBound, setIsWechatBound] = useState<boolean | null>(null);
  const [wechatLoading, setWechatLoading] = useState(true);
  
  // 修改密码相关状态
  const [passwordForm] = Form.useForm();
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // 提示消息状态
  const [profileAlert, setProfileAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [passwordAlert, setPasswordAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // 获取用户资料
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await userApi.getDoctorProfile();
        if (response.success && response.data) {
          setUserProfile(response.data);
          form.setFieldsValue({
            name: response.data.name,
            title: response.data.title
          });
        } else {
          message.error('获取个人资料失败');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        message.error('网络错误，无法获取个人资料');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [form]);

  // 检查微信绑定状态
  const checkWechatBinding = async () => {
    try {
      const response = await userApi.checkWechatBinding();
      if (response.success) {
        setIsWechatBound(response.data.is_bound);
      } else {
        message.error('查询微信绑定状态失败');
      }
    } catch (error) {
      console.error('Error checking WeChat binding:', error);
      message.error('网络错误，无法查询微信绑定状态');
    } finally {
      setWechatLoading(false);
    }
  };

  // 初始化检查微信绑定状态
  useEffect(() => {
    checkWechatBinding();
  }, []);

  // 使用useInterval每秒钟轮询微信绑定状态，直到绑定成功
  useInterval(checkWechatBinding, 1000, isWechatBound === false || isWechatBound === null);

  // 修改密码处理函数
  const handleChangePassword = async (values: any) => {
    // 验证两次输入的新密码是否匹配
    if (values.newPassword !== values.confirmNewPassword) {
      setPasswordAlert({ type: 'error', message: '两次输入的新密码不匹配' });
      return;
    }

    setPasswordLoading(true);
    try {
      // 对密码进行SHA256哈希处理
      const [oldPasswordHash, newPasswordHash] = await Promise.all([
        getPasswordHash(values.oldPassword),
        getPasswordHash(values.newPassword)
      ]);

      const response = await userApi.changePassword({
        old_password_hash: oldPasswordHash,
        new_password_hash: newPasswordHash
      });

      if (response.success) {
        setPasswordAlert({ type: 'success', message: '密码修改成功' });
        passwordForm.resetFields();
      } else {
        setPasswordAlert({ type: 'error', message: response.message || '密码修改失败' });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordAlert({ type: 'error', message: '网络错误，无法修改密码' });
    } finally {
      setPasswordLoading(false);
    }
  };

  // 提交更新
  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const updateData: UpdateDoctorProfileRequest = {
        name: values.name,
        title: values.title
      };

      const response = await userApi.updateDoctorProfile(updateData);
      if (response.success) {
        setProfileAlert({ type: 'success', message: '个人资料更新成功' });
        setUserProfile(response.data);
      } else {
        setProfileAlert({ type: 'error', message: response.message || '更新失败，请稍后重试' });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileAlert({ type: 'error', message: '网络错误，更新失败' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !userProfile) {
    return <div>加载中...</div>;
  }

  return (
    <div>
      <h2>个人资料</h2>
      <Card style={{ marginTop: 16 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          {profileAlert && (
            <Alert
              message={profileAlert.message}
              type={profileAlert.type}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="title"
            label="职称"
            rules={[{ required: true, message: '请输入职称!' }]}
          >
            <Input placeholder="例如：主任医师、主治医师" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存修改
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 微信绑定卡片 */}
      <Card title="微信绑定" style={{ marginTop: 16 }} loading={wechatLoading}>
        {isWechatBound === true ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <p style={{ fontSize: 16, color: '#38910c' }}>已绑定微信</p>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Image
              src="/wx-bind.jpg"
              alt="微信小程序码"
              style={{ width: 200, height: 200, backgroundColor: '#f0f0f0', marginBottom: 20 }}
            />
            <p style={{ fontSize: 14, color: '#666' }}>使用微信扫描二维码完成绑定</p>
          </div>
        )}
      </Card>

      {/* 修改密码卡片 */}
      <Card title="修改密码" style={{ marginTop: 16 }}>
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          {passwordAlert && (
            <Alert
              message={passwordAlert.message}
              type={passwordAlert.type}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item
            name="oldPassword"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码!' }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码!' },
              { min: 6, message: '密码长度不能少于6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirmNewPassword"
            label="重新输入新密码"
            rules={[{ required: true, message: '请重新输入新密码!' }]}
          >
            <Input.Password placeholder="请重新输入新密码" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={passwordLoading}>
                修改密码
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Profile;